import { Dropdown } from 'bootstrap';

/**
 * Inizializza una o più dropdown di filtro (con o senza fetch).
 *
 * @param {string} containerSelector - Selettore per trovare uno o più contenitori di dropdown.
 * @param {Object} options
 * @param {boolean} options.useFetch - Se true, esegue il fetch iniziale.
 * @param {string} [options.endpoint] - Endpoint da usare se useFetch è true.
 * @param {string} [options.paramName='tags[]'] - Nome dei parametri da leggere dalla URL.
 */
export default function initMultiDropdown(containerSelector, {
    useFetch = true,
    endpoint = '/wp-json/wp/v2/tags?orderby=count&order=desc&per_page=100',
    paramName = 'tags[]',
} = {}) {

    const containers = document.querySelectorAll(containerSelector);
    if (!containers.length) return;

    containers.forEach(dropdownContainer => {
        const selectedIds = new Set();
        // helper: normalizza sempre a stringa
        const norm = (v) => String(v);

        const urlParams = new URLSearchParams(window.location.search);

        for (const [key, value] of urlParams.entries()) {
            if (
                key === paramName ||                       // es. "tags[]"
                key.startsWith(paramName.replace('[]', '[')) // es. "tags[0]", "tags[1]"
            ) {
                selectedIds.add(norm(value));
            }
        }


        const toggleBtn = dropdownContainer.querySelector('.dropdown-toggle');
        const dropdownMenu = dropdownContainer.querySelector('.dropdown-menu');
        const searchInput = dropdownContainer.querySelector('input[type="search"], input[type="text"]');
        const listContainer = dropdownContainer.querySelector('.dropdown-list');
        const countBadge = dropdownContainer.querySelector('.selectedCountBadge');
        const pillContainer = dropdownContainer.querySelector('.selectedPillsContainer');

        if (!toggleBtn || !dropdownMenu || !listContainer)
            return;

        new Dropdown(toggleBtn, { autoClose: 'outside' });

        let allItems = [];

        // --- ⬇ FETCH o inizializzazione
        if (useFetch) {
            fetch(endpoint)
                .then(res => res.json())
                .then(data => {
                    allItems = data.map(t => ({ ...t, id: norm(t.id) }));
                    renderItems(allItems);
                    renderSelected();
                    updateCount();
                });
        } else {

            // Legge da DOM
            const items = listContainer.querySelectorAll('label');
            items.forEach(label => {
                const input = label.querySelector('input[type="checkbox"]');
                if (!input) return;
                const id = norm(input.value);
                const name = label.textContent.trim();

                allItems.push({ id, name });

                if (input.checked) {
                    selectedIds.add(id);
                }

                // Re-attacca il listener per sincronizzare selezioni
                input.addEventListener('click', e => {
                    e.stopPropagation();
                    if (input.checked) {
                        selectedIds.add(id);
                    } else {
                        selectedIds.delete(id);
                    }
                    renderSelected();
                    updateCount();
                });
            });
            renderSelected();
            updateCount();
        }

        function renderItems(items) {

            console.log(items);

            listContainer.innerHTML = '';

            const selected = items.filter(item => selectedIds.has(item.id));
            const unselected = items.filter(item => !selectedIds.has(item.id));
            const ordered = [...selected, ...unselected];

            ordered.forEach(item => {
                const label = document.createElement('label');
                label.className = 'custom-square-checkbox mb-2 d-block';

                const checkbox = document.createElement('input');
                checkbox.type = 'checkbox';
                checkbox.name = paramName;
                checkbox.value = item.id;
                checkbox.checked = selectedIds.has(item.id);

                checkbox.addEventListener('click', e => {
                    e.stopPropagation();
                    if (checkbox.checked) {
                        selectedIds.add(item.id);
                    } else {
                        selectedIds.delete(item.id);
                    }
                    renderSelected();
                    updateCount();
                });

                const visual = document.createElement('span');
                const text = document.createTextNode(' ' + item.name);

                label.appendChild(checkbox);
                label.appendChild(visual);
                label.appendChild(text);
                listContainer.appendChild(label);
            });
        }

        function renderSelected() {
            if (!pillContainer) return;

            pillContainer.innerHTML = '';
            pillContainer.classList.toggle('mb-2', selectedIds.size > 0);

            console.log(selectedIds);

            const missingIds = [...selectedIds].filter(
                id => !allItems.some(tag => tag.id === id)
            );

            if (useFetch && missingIds.length > 0) {
                Promise.all(
                    missingIds.map(id =>
                        fetch(`/wp-json/wp/v2/tags/${parseInt(id, 10)}`).then(res => res.json()).catch(() => null)
                    )
                ).then(results => {
                    const valid = results.filter(tag => tag && tag.id);
                    allItems.push(...valid);
                    renderItems(allItems);
                    renderSelected();
                    updateCount();
                });
                return;
            }

            const selected = allItems.filter(tag => selectedIds.has(tag.id));

            selected.forEach(tag => {
                const badge = document.createElement('span');
                badge.className = 'filter-pill me-2 mb-1';
                badge.innerHTML = `${tag.name}<i class="ms-2 fa-sharp fa-lg fa-solid fa-circle-xmark btn-remove-tag" aria-hidden="true"></i>`;

                badge.querySelector('i').addEventListener('click', () => {
                    selectedIds.delete(tag.id);
                    renderItems(allItems);
                    renderSelected();
                    updateCount();
                });

                pillContainer.appendChild(badge);
            });
        }

        function updateCount() {
            if (!countBadge) return;
            const count = selectedIds.size;
            countBadge.textContent = count;
            countBadge.classList.toggle('d-none', count === 0);
        }

        // Live search (solo se c'è input e fetch abilitato)
        if (useFetch && searchInput) {
            searchInput.addEventListener('input', () => {
                const term = searchInput.value;
                fetch(`/wp-json/wp/v2/tags?search=${encodeURIComponent(term)}&orderby=count&order=desc`)
                    .then(res => {
                        if (!res.ok) {
                            throw new Error('HTTP error ' + res.status);
                        }
                        return res.json();
                    })
                    .then(fetched => {
                        fetched.forEach(tag => {
                            if (!allItems.find(t => t.id === tag.id)) {
                                allItems.push(tag);
                            }
                        });

                        const selected = allItems.filter(tag => selectedIds.has(tag.id));
                        const combined = [...selected, ...fetched.filter(t => !selectedIds.has(t.id))];
                        renderItems(combined);
                    });
            });
        }

        // Live search anche per dropdown SENZA fetch
        if (!useFetch && searchInput) {
            searchInput.addEventListener('input', () => {
                const term = searchInput.value.trim().toLowerCase();

                const filtered = allItems.filter(item =>
                    item.name.toLowerCase().includes(term)
                );

                renderItems(filtered);

            });
        }

    });
}